export type IRetractionOccurrence = {
  text: string;
  reasonsForRetraction: string;
  retractionDate: string;
  retractionNature: string;
  link: {
    text: string;
    href: string;
    eventName: string;
  };
  linkPriority: boolean;
};

export type ITorturedPhraseOccurrence = {
  text: string;
  title: string;
  expectedText: string;
  status: EResultStatus;
};

export type IAffiliationOccurrence = {
  title: string;
  text: string;
  status: EResultStatus;
  organizationType?: string;
  formattedLocation?: string;
  link?: {
    text: string;
    href: string;
    eventName: string;
  };
  linkPriority?: boolean;
};

export type IGenericOccurrence = {
  title?: string;
  text?: string;
  status?: EResultStatus;
  list?: {
    key?: string;
    text: string;
  }[];
  eventName?: string;
  link?: {
    text: string;
    href: string;
    eventName: string;
  };
  linkPriority?: boolean;
  subtitle?: string;
};

export type ICheckResultUpdateCondition = {
  checkType?: ECheckType;
  submissionId?: string;
  submissionContextId?: string;
  roundId?: string;
  externalId?: string;
  runId?: string;
};

export enum EResultStatus {
  GREEN = 'GREEN',
  RED = 'RED',
  BROWN = 'BROWN',
  ORANGE = 'ORANGE'
}

export enum EIntegrityCheckOverallStatus {
  GREEN = 2,
  AMBER = 1,
  RED = 0,
  WHITE = 3
}

export const IntegrityCheckOverallStatusMap: Record<
  EIntegrityCheckOverallStatus,
  string
> = {
  0: 'RED',
  1: 'ORANGE',
  2: 'GREEN',
  3: 'WHITE'
};

export type IResult = {
  status: EResultStatus;
  title: string;
  subtitle: string;
  showDetailedReport?: boolean;
  occurrences:
    | IRetractionOccurrence[]
    | ITorturedPhraseOccurrence[]
    | IAffiliationOccurrence[]
    | IGenericOccurrence[]
    | null;
  hasMailTo?: boolean;
  configs: Record<string, string>;
  links?: {
    text: string;
    href: string;
    eventName: string;
  }[];
  linkPriority?: boolean;
};

export type ICheckError = {
  errorMessage?: string;
  errorCode?: string | number;
  hasMailTo?: boolean;
  [key: string]: any;
};

export enum ECheckLifecycleStatus {
  PROCESSING = 'PROCESSING',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
  NOT_STARTED = 'NOT_STARTED'
}

export type IIntegrityCheckManualRunResponse = {
  externalId: string;
  message?: string;
};

export enum ECheckType {
  IMAGE_DUPLICATION = 'image_duplication',
  SIMILARITY_SCORE = 'similarity_check',
  RETRACTED_PAPERS = 'retracted_papers',
  TORTURED_PHRASES = 'tortured_phrases',
  OVER_CITATIONS = 'over_citations',
  AFFILIATION_CHECK = 'affiliation_check',
  EMAIL_AFFILIATION_CHECK = 'email_affiliation_check',

  REFERENCES_UNVERIFIED_JOURNALS = 'references_unverified_journals',
  REFERENCES_WITH_TORTURED_PHRASES = 'references_with_tortured_phrases',
  REFERENCE_DUPLICATION = 'reference_duplication',
  REFERENCE_COUNT = 'reference_count',
  REFERENCES_VALIDITY_ACCESSIBILITY = 'references_validity_accessibility',
  FABRICATED_CITATIONS = 'fabricated_citations',
  PROBLEMATIC_CITATIONS = 'problematic_citations',
  OLD_REFERENCES = 'old_references',
  NOT_PUBLISHED_REFERENCES = 'not_published_references',
  REFERENCE_CITATIONS = 'reference_citations',
  REFERENCE_HISTOGRAM = 'reference_histogram',
  REFERENCES_RATE = 'references_rate',
  REFERENCE_AGE_FUTURE = 'reference_age_future',
  SELF_CITING = 'self_citing',

  AI_DETECTION = 'ai_detection',
  AI_USAGE_DISCLOSURE = 'ai_usage_disclosure',
  AI_PROMPTS = 'ai_prompts',

  AUTHORS_ORCID_VALIDITY = 'authors_orcid_validity',
  AUTHORS_WITH_RETRACTED_PAPERS = 'authors_with_retracted_papers',
  AUTHORS_WITH_TORTURED_PHRASES = 'authors_with_tortured_phrases',
  AUTHORSHIP_NETWORKS = 'authorship_networks',
  AUTHOR_STATEMENT = 'author_statement',
  AUTHORS_CONTRIBUTIONS = 'authors_contributions',

  ETHICS_STATEMENT = 'ethics_statement',
  DATA_ACCESS_STATEMENT = 'data_access_statement',
  CONFLICT_OF_INTEREST = 'conflict_of_interest',
  CLINICAL_TRIAL = 'clinical_trial',

  STRUCTURED_ABSTRACT = 'structured_abstract',
  ABSTRACT_LENGTH = 'abstract_length',
  CITATIONS_IN_ABSTRACT = 'citations_in_abstract',

  CORRESPONDING_AUTHOR_EMAIL = 'corresponding_author_email',
  FUNDING_STATEMENT = 'funding_statement',
  FUNDER_VALIDATION = 'funder_validation',

  KEYWORDS = 'keywords',
  MANUSCRIPT_LENGTH = 'manuscript_length',
  MANUSCRIPT_STRUCTURE = 'manuscript_structure',
  MANUSCRIPT_LANGUAGE = 'manuscript_language',

  TITLE_LENGTH = 'title_length',

  FIGURE_LEGEND = 'figure_legend',
  FIGURES_CITATIONS = 'figures_citations',
  TABLE_LEGEND = 'table_legend',
  TABLE_CITATIONS = 'table_citations',

  COPYRIGHTED_METHODS = 'copyrighted_methods',
  EXCESSIVE_BRANDING = 'excessive_branding',
  LANGUAGE_QUALITY_CHECK = 'language_quality_check',
  AFFILIATION_VERIFICATION = 'affiliation_verification'
}

export const CheckTypeMap: Record<ECheckType, string> = {
  over_citations: 'Over-Citations',
  tortured_phrases: 'Tortured Phrases',
  affiliation_check: 'Institution Verification',
  email_affiliation_check: 'Email Domain Verification',
  affiliation_verification: 'Affiliation Verification',

  retracted_papers: 'Retracted Papers in References',
  image_duplication: 'Image Verification',
  similarity_check: 'Similarity Score',

  references_unverified_journals: 'References in Non-Indexed Journals',
  references_with_tortured_phrases: 'References with Tortured Phrases',
  reference_duplication: 'Duplicate References',
  reference_count: 'Number of References',
  references_validity_accessibility: 'References Validity and Accessibility',
  fabricated_citations: 'References Validity',
  problematic_citations: 'Problematic Citations',
  old_references: 'Old References',
  not_published_references: 'Not Published References',
  reference_citations: 'References Citation',
  reference_histogram: 'References Time Distribution',
  references_rate: 'References Rate',
  reference_age_future: 'Reference Age in the Future',
  self_citing: 'Excessive Self-Citations',

  ai_detection: 'AI Generated Text',
  ai_usage_disclosure: 'AI Usage Statement',
  ai_prompts: 'AI Prompts',

  authors_orcid_validity: 'ORCID Validity',
  authors_with_retracted_papers: 'Authors with Retracted Papers',
  authors_with_tortured_phrases: 'Authors of Papers with Tortured Phrases',
  authorship_networks: 'Authorship Network',
  author_statement: 'Authors Contribution Statement',
  authors_contributions: 'Manuscript Authors',

  ethics_statement: 'Ethics Statement',
  data_access_statement: 'Data Access Statement',
  conflict_of_interest: 'Conflict of Interests Statement',
  clinical_trial: 'Clinical Trial Registration',

  structured_abstract: 'Abstract Structure',
  abstract_length: 'Abstract Length',
  citations_in_abstract: 'Citations in Abstract',

  corresponding_author_email: 'Authors Emails',
  funding_statement: 'Funding Statement',
  funder_validation: 'Funder Validation',

  keywords: 'Number of Keywords',
  manuscript_length: 'Manuscript Length',
  manuscript_structure: 'Manuscript Structure',
  manuscript_language: 'Manuscript Language (EN)',

  title_length: 'Title Length',

  figure_legend: 'Figures Legends',
  figures_citations: 'Figures Citation',
  table_legend: 'Tables Legends',
  table_citations: 'Tables Citation',

  copyrighted_methods: 'Copyrighted Methods',
  excessive_branding: 'Branding Elements',
  language_quality_check: 'Language Quality'
};

export type ICheckResult = {
  submissionId: string;
  submissionContextId?: string;
  checkType: ECheckType;
  roundId: string;
  status: ECheckLifecycleStatus;
  manuscriptFileUrl: string;
  titlePageFileUrl: string;
  result?: IResult;
  error?: ICheckError;
  externalId?: string | null;
  runId?: string;
  created_at?: string;
};

export type IIntegrityCheckProcessPayload = {
  articleWorkflowId: string;
  submissionId: string;
  manuscriptFileUrl: string;
  titlePageFileUrl: string;
  checkList: ECheckType[];
};

export type IIntegrityCheckManualRunPayload = {
  submissionId: string;
  revisionNumber: string;
  checks: ECheckType[];
  runId: string;
};

export type IIntegrityCheckInternalPayload = {
  submissionId: string;
  submissionContextId: string;
  roundId: string;
  checkIds?: ECheckType[];
};

export type IIntegrityCheckResultUpdatePayload = {
  submissionId: string;
  submissionContextId: string;
  roundId: string;
  checkType: ECheckType;
  status: ECheckLifecycleStatus;
  result: IResult;
};

export enum ECentralizedResponseStatus {
  SUCCESS = 'SUCCESS',
  IN_PROGRESS = 'In Progress',
  FAILED = 'FAILED'
}
export type TransferedDetail = {
  fromSubmissionContextId: string;
  toSubmissionContextId: string;
  date: string;
  fromJournalName: string;
  toJournalName: string;
  fromManuscriptId: string;
  toManuscriptId: string;
};

export type TransferRecord = {
  transferedDetail: TransferedDetail;
  overallStatus: EIntegrityCheckOverallStatus;
  result: ICheckResultResponse[];
};
export type ICentralizedIntegrityChecksResponse = {
  uniqueId: string | null;
  profileId: string;
  status: ECentralizedResponseStatus;
  statusCode: number;
  extraction: string;
  response:
    | {
        checkId: ECheckType;
        originalStatus: EResultStatus;
        title: string;
        subtitle: string;
        occurrences:
          | IRetractionOccurrence[]
          | ITorturedPhraseOccurrence[]
          | IAffiliationOccurrence[]
          | IGenericOccurrence[]
          | null;
        hasMailto: boolean;
        configs: Record<string, string>;
        links?: {
          text: string;
          href: string;
          eventName: string;
        }[];
        linkPriority?: boolean;
      }[]
    | string;
  createdAt: string;
  titleFileUrl: string;
  manuscriptFileUrl: string;
  checkIds: ECheckType[];
};

export type ICheckResultResponse = {
  submissionId: string;
  submissionContextId: string;
  failedCheckCount: number;
  checkCount: number;
  roundId: string;
  checkResults: (ICheckResult & { runType: string })[];
  overallStatus: EIntegrityCheckOverallStatus;
};

export type IDetailedCheckReportResponse = {
  fileUrl: string;
};

export type IIntegrityOverviewResponse = {
  overallStatus: EIntegrityCheckOverallStatus;
  result: ICheckResultResponse[];
  pastJournalHistory: TransferRecord[];
};

export type ICustomChecksResponse = {
  result: ICheckResultResponse[];
};

export type IIntegrityRoundGroup = {
  submissionContextId: string;
  roundId: string;
  titlePageFileUrl: string;
  manuscriptFileUrl: string;
  checkResults: ICheckResult[];
};
